// ============================================================================
// Bounce Matrix — central navigation gating (FR28 / Story 4.3)
// ----------------------------------------------------------------------------
// resolveRoute() is the SINGLE source of truth for gating redirects. Precedence:
//
//   auth  >  access_status  >  admin  >  onboarding  >  default
//   (admin vem antes do onboarding: quem administra precisa do painel
//    mesmo sem ter completado a própria persona)
//
// Bounce table (current route  ->  destination):
//
//   | user state                         | private app route | onboarding route | public/auth route |
//   |------------------------------------|-------------------|------------------|-------------------|
//   | not logged in                      | /login?back=<r>   | /login?back=<r>  | stay              |
//   | access_status pending/blocked (*)  | /aguardando       | /aguardando      | stay              |
//   | onboarding = pendente              | /onboarding/brand-persona            | stay (if target) |
//   | onboarding = brand_persona_pronta  | /onboarding/avatar                   | stay (if target) |
//   | onboarding = concluido             | stay              | stay             | -> home           |
//   | logged in on /login or /signup     | -                 | -                | -> home           |
//   | non-admin on /admin (*)            | /dashboard        | -                | -                 |
//
//   (*) access_status and admin gates are wired but only fire once the backend
//       exposes those fields (Epic 2). Until then they are inert.
//
// Loop-prevention: if a computed destination equals the current route, fall back
// to /dashboard.
//
// CALL-SITE CONVENTION (auditable in review):
//   - Gating redirects use ONLY guardNavigate() / <ProtectedRoute>.
//   - Raw navigate() is reserved for direct user actions (open agent, open
//     builder, create avatar, etc.), never for auth/onboarding/access gating.
//   Authorized gating call-sites:
//     * components/ProtectedRoute.jsx  (route-level guard)
//     * pages/Login.jsx                (post-login home resolution via ?back)
// ============================================================================

const PUBLIC_ROUTES = ["/", "/login", "/signup"];
const AUTH_PAGES = ["/login", "/signup"];
const WAITING_ROUTE = "/aguardando";

// Route prefixes that require auth but NOT completed onboarding.
const ONBOARDING_PREFIXES = [
  "/onboarding/brand-persona",
  "/onboarding/avatar",
  "/avatars/new",
];
// Dynamic onboarding routes: /avatars/:id/builder and /avatars/:id/review.
const ONBOARDING_DYNAMIC = [/^\/avatars\/[^/]+\/(builder|review)$/];

// Route prefixes that require auth AND completed onboarding.
const APP_PREFIXES = ["/dashboard", "/agent", "/avatars", "/library", "/calendario", "/marca"];

const ADMIN_PREFIXES = ["/admin"];

// Account/profile surface: requires auth but NOT completed onboarding — a fresh
// collaborator (invited before finishing their own persona) must reach it to
// manage their account and accept invites (Epic 10.4).
const PROFILE_PREFIXES = ["/perfil"];

function pathOf(route) {
  const r = route || "/";
  const q = r.indexOf("?");
  return q === -1 ? r : r.slice(0, q);
}

function searchOf(route) {
  const r = route || "";
  const q = r.indexOf("?");
  return q === -1 ? "" : r.slice(q);
}

function startsWithAny(path, prefixes) {
  return prefixes.some((p) => path === p || path.startsWith(p + "/"));
}

export function classifyRoute(route) {
  const path = pathOf(route);
  const isPublic = PUBLIC_ROUTES.includes(path);
  const isAuthPage = AUTH_PAGES.includes(path);
  const isWaiting = path === WAITING_ROUTE;
  const isAdmin = startsWithAny(path, ADMIN_PREFIXES);
  const isProfile = startsWithAny(path, PROFILE_PREFIXES);
  const isOnboarding =
    startsWithAny(path, ONBOARDING_PREFIXES) ||
    ONBOARDING_DYNAMIC.some((re) => re.test(path));
  // App routes require completed onboarding, but onboarding routes (which share
  // the /avatars prefix) must be excluded so they don't demand completion.
  const isApp = startsWithAny(path, APP_PREFIXES) && !isOnboarding;

  return {
    path,
    isPublic,
    isAuthPage,
    isWaiting,
    isAdmin,
    isProfile,
    isOnboarding,
    isApp,
    requiresAuth: isApp || isOnboarding || isAdmin || isWaiting || isProfile,
    requiresOnboarding: isApp || isAdmin,
    requiresAdmin: isAdmin,
  };
}

export function hasAccessBlock(user) {
  return Boolean(user?.access_status && user.access_status !== "allowed");
}

export function isAdmin(user) {
  if (!user) return false;
  if (Array.isArray(user.roles)) return user.roles.includes("admin");
  return user.role === "admin";
}

// A filled Brand Persona is still a draft until the user explicitly confirms
// it. Completed accounts predate this flag, so their existing access must not
// be downgraded while the API projects legacy records.
export function needsPersonaConfirmation(user) {
  // A collaborator reads the owner's workspace; their own unfinished persona
  // must not block an already-authorized avatar builder/review deep link.
  if (!user || isViewer(user) || user.status_onboarding === "concluido") return false;
  return user.persona_confirmada !== true || user.status_onboarding !== "brand_persona_pronta";
}

// ── Shared-workspace context (Epic 10) ──────────────────────────────────────
// `/auth/me` returns `active_workspace {owner_user_id, marca, role} | null`
// (null = the user's own account) plus `pending_invites_count`. These helpers
// are the single source of truth the UI uses to decide read-only gating.

export function activeWorkspace(user) {
  return user?.active_workspace || null;
}

// A viewer is any user currently operating inside someone else's workspace.
// Role defaults to "viewer" (the only collaborator role today) so a present-but-
// role-less active_workspace still gates writes read-only.
export function isViewer(user) {
  const ws = activeWorkspace(user);
  return Boolean(ws) && (ws.role || "viewer") === "viewer";
}

export function pendingInvitesCount(user) {
  const n = Number(user?.pending_invites_count);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

// Where a logged-in user belongs when they have no explicit destination.
export function userHome(user) {
  if (!user) return "/login";
  if (hasAccessBlock(user)) return WAITING_ROUTE;
  if (needsPersonaConfirmation(user)) return "/onboarding/brand-persona";
  if (user.status_onboarding === "pendente") return "/onboarding/brand-persona";
  if (user.status_onboarding === "brand_persona_pronta") return "/onboarding/avatar";
  return "/dashboard";
}

// Returns a redirect destination string, or null if the user may stay on `route`.
export function resolveRoute({ user, route }) {
  const cls = classifyRoute(route);
  const path = cls.path;

  // 1. auth
  if (cls.requiresAuth && !user) {
    const back = encodeURIComponent(path + searchOf(route));
    return `/login?back=${back}`;
  }

  // Logged-in user landing on a marketing/auth page -> send home.
  if (user && cls.isAuthPage) {
    const home = userHome(user);
    return home === path ? "/dashboard" : home;
  }

  if (user) {
    // 2. access_status (Epic 2 hook — inert until backend exposes the field)
    if (hasAccessBlock(user)) {
      return path === WAITING_ROUTE ? null : WAITING_ROUTE;
    }
    // Allowed user should not linger on the waiting screen.
    if (cls.isWaiting) {
      const home = userHome(user);
      return home === path ? "/dashboard" : home;
    }

    // 3. admin — antes do onboarding: admin opera o painel mesmo sem persona.
    if (cls.requiresAdmin) {
      return isAdmin(user) ? null : "/dashboard";
    }

    // 4. onboarding
    // Avatar creation/review must never become a shortcut past an unconfirmed
    // Brand Persona. The persona route itself remains available so users with
    // an unfinished avatar can intentionally return to their conversation.
    if (cls.isOnboarding && needsPersonaConfirmation(user) && path !== "/onboarding/brand-persona") {
      return "/onboarding/brand-persona";
    }
    if (cls.requiresOnboarding && user.status_onboarding !== "concluido") {
      const home = userHome(user);
      return home === path ? "/dashboard" : home; // loop-prevention
    }
  }

  // default: stay
  return null;
}

// Imperative gating helper. Given the route the user is trying to reach, either
// navigate there (allowed) or to the gate's destination. Returns the final path.
export function guardNavigate(navigate, { user, route, replace = true }) {
  const dest = resolveRoute({ user, route });
  const target = dest || route;
  navigate(target, { replace });
  return target;
}

// Read a sanitized ?back= target from a location.search string. Only same-origin
// absolute paths are honored (prevents open-redirect via ?back=http://evil).
export function backTarget(search, fallback = "/dashboard") {
  try {
    const params = new URLSearchParams(search || "");
    const back = params.get("back");
    if (back && back.startsWith("/") && !back.startsWith("//")) return back;
  } catch {
    /* ignore malformed search */
  }
  return fallback;
}
